import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Clock, ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { submitContact } from "@/lib/api";

const EMAILS = [
  { label: "General", email: "hello@bigeyedeassystems.com" },
  { label: "Proposals", email: "proposals@bigeyedeassystems.com" },
  { label: "Support", email: "support@bigeyedeassystems.com" },
];

const fieldClass =
  "bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500 rounded-xl h-12 focus-visible:ring-cyan-400/40";

export default function Contact() {
  const [form, setForm] = useState({ full_name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Contact | Brand-in-a-Box";
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Message received. We'll reply within one business day.");
      setForm({ full_name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#05060c] min-h-screen">
      <section className="relative pt-20 pb-12 md:pt-32 overflow-hidden">
        <div className="absolute inset-0 bg-aurora opacity-50" aria-hidden />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
          <span className="font-mono text-[11px] font-semibold tracking-widest uppercase text-cyan-400">
            Contact
          </span>
          <h1 className="mt-3 text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-white leading-tight">
            Let's talk about your <span className="gradient-text-cyan">infrastructure.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed">
            Send a message, or use one of the addresses below. Expected response time is within one business day.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <aside className="lg:col-span-1 space-y-6">
            <div className="rounded-2xl glass p-6">
              <h3 className="font-mono text-[11px] font-semibold tracking-widest uppercase text-cyan-400">Email</h3>
              <ul className="mt-3 space-y-3">
                {EMAILS.map((e) => (
                  <li key={e.email} className="flex items-start gap-2 text-sm">
                    <Mail className="h-4 w-4 text-cyan-400 mt-0.5" />
                    <div>
                      <p className="text-slate-500 text-xs">{e.label}</p>
                      <a
                        data-testid={`contact-email-${e.label.toLowerCase()}`}
                        className="text-white font-medium hover:text-cyan-300 break-all"
                        href={`mailto:${e.email}`}
                      >
                        {e.email}
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl glass p-6">
              <h3 className="font-mono text-[11px] font-semibold tracking-widest uppercase text-cyan-400">Response time</h3>
              <p className="mt-3 inline-flex items-start gap-2 text-sm text-slate-200">
                <Clock className="h-4 w-4 text-cyan-400 mt-0.5" />
                Within 1 business day
              </p>
            </div>
            <div className="rounded-2xl glass p-6">
              <h3 className="font-mono text-[11px] font-semibold tracking-widest uppercase text-cyan-400">Business hours</h3>
              <p className="mt-3 text-sm text-slate-200">Mon–Fri, 9:00 – 18:00 GMT</p>
            </div>
          </aside>

          <div className="lg:col-span-2">
            {done ? (
              <div data-testid="contact-success" className="rounded-3xl glass-strong p-10 text-center glow-cyan-soft">
                <h3 className="text-2xl font-extrabold tracking-tight text-white">Message received.</h3>
                <p className="mt-3 text-slate-400">We'll be in touch within one business day.</p>
                <Button
                  data-testid="contact-send-another"
                  variant="outline"
                  onClick={() => setDone(false)}
                  className="mt-6 rounded-full border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-white font-semibold"
                >
                  Send another message
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="rounded-3xl glass-strong p-6 md:p-10"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-200">Full name *</Label>
                    <Input data-testid="contact-input-name" value={form.full_name} onChange={update("full_name")} required className={fieldClass} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-200">Email *</Label>
                    <Input data-testid="contact-input-email" type="email" value={form.email} onChange={update("email")} required className={fieldClass} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-sm font-semibold text-slate-200">Subject *</Label>
                    <Input data-testid="contact-input-subject" value={form.subject} onChange={update("subject")} required className={fieldClass} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-sm font-semibold text-slate-200">Message *</Label>
                    <Textarea
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={update("message")}
                      required
                      rows={6}
                      className="bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500 rounded-xl focus-visible:ring-cyan-400/40"
                    />
                  </div>
                </div>
                <div className="mt-8 flex justify-end">
                  <Button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={submitting}
                    className="rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold px-8 h-12 glow-cyan"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Send Message <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
